import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getRecentAlerts: (limit = 10) => api.get(`/dashboard/recent-alerts?limit=${limit}`),
  getRiskDistribution: () => api.get('/dashboard/risk-distribution'),
  getTransactionTrends: (days = 30) => api.get(`/dashboard/transaction-trends?days=${days}`),
};

// Entity API
export const entityAPI = {
  list: (params) => api.get('/entities', { params }),
  get: (id) => api.get(`/entities/${id}`),
  create: (data) => api.post('/entities', data),
  update: (id, data) => api.put(`/entities/${id}`, data),
  getTransactions: (id, limit = 50) => api.get(`/entities/${id}/transactions?limit=${limit}`),
  getNetwork: (id, depth = 2) => api.get(`/entities/${id}/network?depth=${depth}`),
};

// Transaction API
export const transactionAPI = {
  list: (params) => api.get('/transactions', { params }),
  get: (id) => api.get(`/transactions/${id}`),
  create: (data) => api.post('/transactions', data),
};

// Alert API
export const alertAPI = {
  list: (params) => api.get('/alerts', { params }),
  get: (id) => api.get(`/alerts/${id}`),
  create: (data) => api.post('/alerts', data),
  update: (id, data) => api.put(`/alerts/${id}`, data),
  bulkUpdate: (alertIds, status) => api.post('/alerts/bulk-update', null, { params: { alert_ids: alertIds.join(','), status } }),
};

// Case API
export const caseAPI = {
  list: (params) => api.get('/cases', { params }),
  get: (id) => api.get(`/cases/${id}`),
  create: (data) => api.post('/cases', data),
  update: (id, data) => api.put(`/cases/${id}`, data),
  addNote: (id, note) => api.post(`/cases/${id}/notes`, note),
  bulkUpdate: (data) => api.post('/cases/bulk-update', data),
  bulkDelete: (caseIds) => api.post('/cases/bulk-delete', caseIds),
};

// Watchlist API
export const watchlistAPI = {
  list: () => api.get('/watchlists'),
  getEntries: (id, params) => api.get(`/watchlists/${id}/entries`, { params }),
  screen: (entityName) => api.post(`/watchlists/screen?entity_name=${encodeURIComponent(entityName)}`),
  // Namescan.io Integration
  namescan: {
    status: () => api.get('/watchlists/namescan/status'),
    screenPerson: (name, dateOfBirth, country) => {
      let url = `/watchlists/namescan/screen-person?name=${encodeURIComponent(name)}`;
      if (dateOfBirth) url += `&date_of_birth=${encodeURIComponent(dateOfBirth)}`;
      if (country) url += `&country=${encodeURIComponent(country)}`;
      return api.post(url);
    },
    screenOrganisation: (name, country) => {
      let url = `/watchlists/namescan/screen-organisation?name=${encodeURIComponent(name)}`;
      if (country) url += `&country=${encodeURIComponent(country)}`;
      return api.post(url);
    },
    screenEntity: (entityId) => api.post(`/watchlists/namescan/screen-entity/${entityId}`),
    listScreenings: (params) => api.get('/watchlists/namescan/screenings', { params }),
    getScreening: (id) => api.get(`/watchlists/namescan/screenings/${id}`),
    // Batch screening
    batchScreen: (entities) => api.post('/watchlists/namescan/batch-screen', { entities }),
    batchScreenCSV: (file) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.post('/watchlists/namescan/batch-screen-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    getBatch: (batchId) => api.get(`/watchlists/namescan/batch/${batchId}`),
    listBatches: (params) => api.get('/watchlists/namescan/batches', { params }),
  },
};

// Crypto API
export const cryptoAPI = {
  listWallets: (params) => api.get('/crypto/wallets', { params }),
  getWallet: (address) => api.get(`/crypto/wallets/${address}`),
  createWallet: (data) => api.post('/crypto/wallets', data),
};

// Data Source API
export const dataSourceAPI = {
  list: () => api.get('/data-sources'),
  create: (data) => api.post('/data-sources', data),
  checkHealth: (id) => api.get(`/data-sources/${id}/health`),
  checkAllHealth: () => api.post('/data-sources/check-all'),
};

// AI API
export const aiAPI = {
  analyze: (data) => api.post('/ai/analyze', data),
  generateSAR: (caseId) => api.post(`/ai/generate-sar?case_id=${caseId}`),
  prioritizeCase: (caseId) => api.post(`/ai/prioritize-case/${caseId}`),
  bulkPrioritize: () => api.post('/ai/bulk-prioritize'),
};

// Import API
export const importAPI = {
  entities: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/entities', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  transactions: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/transactions', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Reports API
export const reportsAPI = {
  entityRiskSummary: () => api.get('/reports/entity-risk-summary'),
  transactionSummary: (days = 30) => api.get(`/reports/transaction-summary?days=${days}`),
  alertMetrics: () => api.get('/reports/alert-metrics'),
  generateSAR: (caseId) => `${API_BASE}/reports/sar/${caseId}`,  // Returns URL for download
};

// Export API
export const exportAPI = {
  entities: (format = 'excel') => `${API_BASE}/export/entities?format=${format}`,
  transactions: (format = 'excel') => `${API_BASE}/export/transactions?format=${format}`,
  cases: (format = 'excel') => `${API_BASE}/export/cases?format=${format}`,
};

// Search API
export const searchAPI = {
  global: (params) => api.get('/search', { params }),
};

// User API
export const userAPI = {
  list: () => api.get('/users'),
  update: (id, data) => api.put(`/users/${id}`, data),
  getPreferences: () => api.get('/users/preferences'),
  updatePreferences: (data) => api.put('/users/preferences', data),
};

// 2FA API
export const twoFactorAPI = {
  setup: () => api.post('/auth/2fa/setup'),
  verify: (code) => api.post(`/auth/2fa/verify?code=${code}`),
  disable: (code) => api.post(`/auth/2fa/disable?code=${code}`),
  loginWith2FA: (credentials) => api.post('/auth/login-2fa', credentials),
};

// Scheduled Reports API
export const scheduledReportsAPI = {
  list: () => api.get('/scheduled-reports'),
  create: (data) => api.post('/scheduled-reports', null, { params: data }),
  update: (id, data) => api.put(`/scheduled-reports/${id}`, null, { params: data }),
  delete: (id) => api.delete(`/scheduled-reports/${id}`),
  runNow: (id) => api.post(`/scheduled-reports/${id}/run-now`),
};

// Audit Log API
export const auditAPI = {
  list: (params) => api.get('/audit-logs', { params }),
};

// Notification API
export const notificationAPI = {
  getSettings: () => api.get('/notifications/settings'),
  updateSettings: (data) => api.put('/notifications/settings', data),
  testEmail: (email) => api.post(`/notifications/test-email?email=${encodeURIComponent(email)}`),
  getEmailStatus: () => api.get('/notifications/email-status'),
  getEmailLogs: (limit = 20) => api.get(`/notifications/email-logs?limit=${limit}`),
};

// Email Templates API
export const emailTemplatesAPI = {
  list: () => api.get('/email-templates'),
  get: (id) => api.get(`/email-templates/${id}`),
  create: (data) => api.post('/email-templates', null, { params: data }),
  update: (id, data) => api.put(`/email-templates/${id}`, null, { params: data }),
  delete: (id) => api.delete(`/email-templates/${id}`),
  preview: (id) => api.post(`/email-templates/${id}/preview`),
  sendTest: (id, email) => api.post(`/email-templates/${id}/send-test?email=${encodeURIComponent(email)}`),
  // Versioning
  listVersions: (id, limit = 20) => api.get(`/email-templates/${id}/versions?limit=${limit}`),
  getVersion: (id, versionId) => api.get(`/email-templates/${id}/versions/${versionId}`),
  previewVersion: (id, versionId) => api.post(`/email-templates/${id}/versions/${versionId}/preview`),
  revertToVersion: (id, versionId) => api.post(`/email-templates/${id}/revert/${versionId}`),
  compareVersions: (id, versionA, versionB) => api.get(`/email-templates/${id}/compare?version_a=${versionA}&version_b=${versionB}`),
};

// Analytics API
export const analyticsAPI = {
  getOverview: (days = 30) => api.get(`/analytics/overview?days=${days}`),
  getRiskTrends: (days = 30) => api.get(`/analytics/risk-trends?days=${days}`),
  getNetworkStats: () => api.get('/analytics/network-stats'),
};

// Report Builder API
export const reportBuilderAPI = {
  getSections: () => api.get('/report-builder/sections'),
  listTemplates: () => api.get('/report-builder/templates'),
  getTemplate: (id) => api.get(`/report-builder/templates/${id}`),
  createTemplate: (data) => api.post('/report-builder/templates', data),
  updateTemplate: (id, data) => api.put(`/report-builder/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/report-builder/templates/${id}`),
  generate: (params) => api.post('/report-builder/generate', params, { responseType: 'blob' }),
  getHistory: (limit = 20) => api.get(`/report-builder/history?limit=${limit}`),
};

// A/B Testing API
export const abTestingAPI = {
  list: (params) => api.get('/ab-testing', { params }),
  get: (id) => api.get(`/ab-testing/${id}`),
  create: (data) => api.post('/ab-testing', data),
  update: (id, data) => api.put(`/ab-testing/${id}`, data),
  delete: (id) => api.delete(`/ab-testing/${id}`),
  recordEvent: (id, variant, eventType) => api.post(`/ab-testing/${id}/record-event?variant=${variant}&event_type=${eventType}`),
  selectVariant: (id, userId) => api.get(`/ab-testing/${id}/select-variant${userId ? `?user_id=${userId}` : ''}`),
  promoteWinner: (id, variant) => api.post(`/ab-testing/${id}/promote-winner?variant=${variant}`),
  getDashboard: () => api.get('/ab-testing/dashboard/summary'),
};

// Branding API
export const brandingAPI = {
  get: () => api.get('/branding'),
  getPublic: () => api.get('/branding/public'),
  update: (data) => api.put('/branding', data),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/branding/upload-logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  listLogos: () => api.get('/branding/logos'),
  deleteLogo: (id) => api.delete(`/branding/logos/${id}`),
};

// Scheduled Screening API
export const scheduledScreeningAPI = {
  list: () => api.get('/scheduled-screening'),
  get: (id) => api.get(`/scheduled-screening/${id}`),
  create: (data) => api.post('/scheduled-screening', data),
  update: (id, data) => api.put(`/scheduled-screening/${id}`, data),
  delete: (id) => api.delete(`/scheduled-screening/${id}`),
  runNow: (id) => api.post(`/scheduled-screening/${id}/run-now`),
  getHistory: (id, limit = 20) => api.get(`/scheduled-screening/${id}/history?limit=${limit}`),
  checkDue: () => api.get('/scheduled-screening/check-due'),
};

// WebSocket URL for notifications
export const getWebSocketURL = (userId) => {
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const backendHost = new URL(BACKEND_URL).host;
  return `${wsProtocol}//${backendHost}/ws/${userId}`;
};

export default api;
